import { SyntheticListenerMap } from "@dnd-kit/core/dist/hooks/utilities";

// API Types
export interface CreateMilestoneRequest {
  logo: File;
  inactiveLogo?: File;
  name: string;
  description: string;
  bronzeBadge?: Badge;
  silverBadge?: Badge;
  goldBadge?: Badge;
}

export interface BadgeRequest {
  name: string;
  description: string;
  logo: File;
  criteria: string[];
}

// UI Types
export type BadgeType = "hall-of-fame" | "bronze" | "silver" | "gold" | "community";
export type LevelStatus = "fixed" | "draggable";

export enum BadgeCategory {
  PLANS = "PLANS",
  AVERAGE_PLANS_PER_YEAR = "AVERAGE_PLANS_PER_YEAR",
  POLICIES = "POLICIES",
  CONTACTS = "CONTACTS",
  PENDING = "PENDING",
  PROPOSALS = "PROPOSALS",
  LIFE_BENEFIT = "LIFE_BENEFIT",
  DISABILITY_BENEFIT = "DISABILITY_BENEFIT",
  TOTAL_BENEFIT = "TOTAL_BENEFIT",
  CASH_VALUE = "CASH_VALUE",
  ACTIVITY = "ACTIVITY",
  SOLUTION_COURSES = "SOLUTION_COURSES",
  YEAR_1_COMMISSIONS = "YEAR_1_COMMISSIONS",
  ONGOING_COMMISSIONS = "ONGOING_COMMISSIONS",
  TOTAL_COMMISSIONS = "TOTAL_COMMISSIONS",
  TARGET_PREMIUM = "TARGET_PREMIUM",
  EXCESS_PREMIUM = "EXCESS_PREMIUM",
  TOTAL_PREMIUM = "TOTAL_PREMIUM",
  LOAN_AMOUNT = "LOAN_AMOUNT",
  ADVISOR_REFERRALS = "ADVISOR_REFERRALS",
  PLAN_TYPE = "PLAN_TYPE",
  OTHER = "OTHER",
}

export const BadgeCategoryDisplay = {
  [BadgeCategory.PLANS]: "Plans",
  [BadgeCategory.AVERAGE_PLANS_PER_YEAR]: "Average Plans Per Year",
  [BadgeCategory.POLICIES]: "Policies",
  [BadgeCategory.CONTACTS]: "Contacts",
  [BadgeCategory.PENDING]: "Pending",
  [BadgeCategory.PROPOSALS]: "Proposals",
  [BadgeCategory.LIFE_BENEFIT]: "Life Benefit",
  [BadgeCategory.DISABILITY_BENEFIT]: "Disability Benefit",
  [BadgeCategory.TOTAL_BENEFIT]: "Total Benefit",
  [BadgeCategory.CASH_VALUE]: "Surrender Value",
  [BadgeCategory.ACTIVITY]: "Activity",
  [BadgeCategory.SOLUTION_COURSES]: "Solution Courses",
  [BadgeCategory.YEAR_1_COMMISSIONS]: "Year 1 Commissions",
  [BadgeCategory.ONGOING_COMMISSIONS]: "Ongoing Commissions",
  [BadgeCategory.TOTAL_COMMISSIONS]: "Total Commissions",
  [BadgeCategory.TARGET_PREMIUM]: "Target Premium",
  [BadgeCategory.EXCESS_PREMIUM]: "Excess Premium",
  [BadgeCategory.TOTAL_PREMIUM]: "Total Premium",
  [BadgeCategory.LOAN_AMOUNT]: "Loan Amount",
  [BadgeCategory.ADVISOR_REFERRALS]: "Advisor Referrals",
  [BadgeCategory.PLAN_TYPE]: "Plan Type",
  [BadgeCategory.OTHER]: "Other",
};

export interface BadgeCriteriaRow {
  label: string;
  category: BadgeCategory | null;
  number: number;
  text?: string;
  subCategory?: Array<{ value: string }>;
  planType?: number;
  criteria_id?: string;
}

export interface Badge {
  achievementCriteria: string;
  rewardDescription: string;
  rewardLink: string;
  logo?: File;
  inactiveLogo?: File;
  category: BadgeCriteriaRow[];
  isConfigured: boolean;
}

export interface BadgeWithId {
  id: string;
  type?: BadgeType;
  title: string;
  description: string;
  criteria: string[];
  levelStatus?: LevelStatus;
  rewardDescription?: string;
  link?: string;
  category?: string;
  number?: string;
  badgeLogo?: string;
}

export interface EditBadge {
  id: string;
  levelStatus: string;
  criteria: string[];
  rewardDescription: string;
  link: string;
  badgeLogo: string;
  category: string;
  number: string;
}

export interface FixedBadge {
  id: string;
  name: string;
  achieved?: number;
  achievementCriteria: string;
  logo: string;
  inactiveLogo?: string;
  reward: string;
  rewardDescription: string;
  rewardLink: string;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  updatedBy: string;
  milestoneId: string | null;
  BadgeCriteria: BadgeCriteria[];
  isCompleted?: boolean;
  completed?: boolean;
}

export interface MilestoneProgress {
  id: string;
  currentValue: number;
  manualOverride?: boolean;
}

export interface BadgeCriteria {
  id: string;
  badgeId: string;
  label: string;
  category: string;
  number: any;
  achieved?: number | null;
  text: string | null;
  completed: boolean;
  completedBy: string | null;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  updatedBy: string;
  milestoneProgresses?: MilestoneProgress[];
}

export interface MilestoneCardProps {
  milestone: MilestoneBadge;
  onEdit?: (milestone: MilestoneBadge) => void;
  onDelete?: (id: string) => void;
  editable?: boolean;
  deletable?: boolean;
  draggable?: boolean;
  dragHandleListeners?: SyntheticListenerMap;
  description?: string;
}

export interface MilestoneBadge {
  id: string;
  name: string;
  achievementCriteria: string;
  description?: string;
  logo: string;
  inactiveLogo?: string;
  reward: string;
  rewardDescription: string;
  rewardLink: string;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  updatedBy: string;
  milestoneId: string;
  BadgeCriteria: BadgeCriteria[];
  Badges?: FixedBadge[];
}

export interface DraggableBadge {
  id: string;
  type: BadgeType;
  title: string;
  description: string;
  criteria: string[];
  levelStatus: LevelStatus;
}

export interface Milestone {
  id: string;
  name: string;
  description: string;
  logo: string;
  order: number;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  updatedBy: string;
  Badges: MilestoneBadge[];
}

export interface CreateMilestonePageProps {
  id?: string;
}
